import React, { useState, useEffect } from "react";
import Modal from "@mui/material/Modal";
import MDInput from "components/MDInput";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import Box from "@mui/material/Box";
import { useAuth } from "context";
import PropTypes from "prop-types";
import MDTypography from "components/MDTypography";
import SimpleBackdrop from "components/MDLoader_Backdrop";
import { Close } from "@mui/icons-material";

const UpdateNameModal = ({
  open,
  onConfirm,
  loader,
  handleClose,
  initialFirstName = "",
  initialLastName = "",
  title = "Almost there!",
  subtitle = "Add your first name and last name to complete setup",
  removeOnClose = false,
}) => {
  const { darkMode } = useAuth();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [firstNameError, setFirstNameError] = useState("");

  useEffect(() => {
    if (open) {
      setFirstName(initialFirstName);
      setLastName(initialLastName);
      setFirstNameError("");
    }
  }, [open, initialFirstName, initialLastName]);

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "400px",
    maxWidth: "400px",
    bgcolor: !darkMode ? "#fff" : "#202940",
    boxShadow: 10,
    p: "24px",
    borderRadius: "12px",
    "@media (max-width:767px)": {
      width: "90%",
      padding: `18px`,
    },
  };

  const handleFormFieldChange = (event) => {
    const { name, value } = event.target;
    if (name === "firstName") {
      setFirstName(value);
      if (value?.trim().length < 2) {
        setFirstNameError("First name should be at least 2 characters.");
      } else {
        setFirstNameError("");
      }
    } else if (name === "lastName") {
      setLastName(value);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!firstName.trim() || firstName.trim().length < 2) {
      setFirstNameError("First name should be at least 2 characters.");
      return;
    }
    onConfirm({ firstName: firstName.trim(), lastName: lastName.trim() });
  };

  return (
    <>
      <Modal
        open={open}
        onClose={removeOnClose ? null : handleClose}
        aria-labelledby="update-name-modal-title"
        aria-describedby="update-name-modal-description"
      >
        <Box sx={style}>
          <MDBox
            sx={{
              display: "flex",
              alignItems: "start",
              justifyContent: "space-between",
            }}
            mb={3}
          >
            <MDBox>
              <MDTypography
                id="update-name-modal-title"
                sx={{ fontSize: "24px", color: "#181D27", fontWeight: 600 }}
              >
                {title}
              </MDTypography>
              {subtitle && (
                <MDTypography sx={{ fontSize: "14px", color: "#475467", lineHeight: "20px" }}>
                  {subtitle}
                </MDTypography>
              )}
            </MDBox>

            <div style={{ cursor: "pointer" }} onClick={handleClose}>
              <Close />
            </div>
          </MDBox>

          <form onSubmit={handleSubmit}>
            <MDBox sx={{ display: "flex", gap: "20px", flexDirection: "column" }}>
              <div>
                <MDTypography
                  sx={{
                    mb: "6px",
                    lineHeight: "20px",
                    color: "#414651",
                    fontSize: "14px",
                    fontWeight: 500,
                  }}
                >
                  First name*
                </MDTypography>
                <MDInput
                  required
                  type="text"
                  name="firstName"
                  variant="outlined"
                  fullWidth
                  value={firstName}
                  onChange={handleFormFieldChange}
                  error={!!firstNameError}
                  helperText={firstNameError}
                  placeholder="Enter your first name"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                    },
                  }}
                />
              </div>

              <div>
                <MDTypography
                  sx={{
                    mb: "6px",
                    lineHeight: "20px",
                    color: "#414651",
                    fontSize: "14px",
                    fontWeight: 500,
                  }}
                >
                  Last name
                </MDTypography>
                <MDInput
                  type="text"
                  name="lastName"
                  variant="outlined"
                  fullWidth
                  value={lastName}
                  onChange={handleFormFieldChange}
                  placeholder="Enter your last name"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                    },
                  }}
                />
              </div>
            </MDBox>

            <MDBox mt="24px">
              <MDButton
                fullWidth
                variant="gradient"
                color="success"
                size="large"
                type="submit"
                disabled={!firstName.trim() || firstNameError || loader}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  fontSize: "16px",
                  fontWeight: 600,
                  height: "48px",
                }}
              >
                {loader ? "Updating..." : "Update"}
              </MDButton>
            </MDBox>
          </form>
        </Box>
      </Modal>
      {loader && <SimpleBackdrop />}
    </>
  );
};

UpdateNameModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onConfirm: PropTypes.func.isRequired,
  loader: PropTypes.bool,
  handleClose: PropTypes.func,
  initialFirstName: PropTypes.string,
  initialLastName: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  removeOnClose: PropTypes.bool,
};

export default UpdateNameModal;
